import { useState, useCallback, useEffect } from 'react';

export const useSwarmHistories = (API_URL, userId, refreshTrigger = 0) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchSessions = useCallback(async () => {
    if (!userId) {
      setSessions([]);
      setHasMore(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/swarm/sessions?limit=20`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to load swarm sessions');
      }
      const data = await response.json();
      const fetched = data.sessions || [];
      setSessions(fetched);
      setHasMore(fetched.length === 20);
    } catch (e) {
      setError(e?.message || 'Failed to load swarm sessions');
    } finally {
      setLoading(false);
    }
  }, [API_URL, userId]);

  const fetchAll = useCallback(async () => {
    if (!userId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/swarm/sessions`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to load swarm sessions');
      }
      const data = await response.json();
      setSessions(data.sessions || []);
      setHasMore(false);
    } catch (e) {
      setError(e?.message || 'Failed to load swarm sessions');
    } finally {
      setLoading(false);
    }
  }, [API_URL, userId]);

  useEffect(() => {
    if (userId) {
      fetchSessions();
    } else {
      setSessions([]);
      setHasMore(true);
    }
  }, [userId, fetchSessions, refreshTrigger]);

  return { sessions, loading, error, refresh: fetchSessions, fetchAll, hasMore };
};
